import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Tooltip,
  Alert,
  Tabs,
  Tab,
  InputAdornment,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Schedule as ScheduleIcon,
  AccountBalance as PayerIcon,
  LocalHospital as TestIcon,
  ExpandMore as ExpandMoreIcon,
  Business as BusinessIcon
} from '@mui/icons-material';
import { FeeSchedule as FeeScheduleType, FeeScheduleRate, PayerContract, PlaceOfServiceRate } from '../../types/Billing';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`fee-tabpanel-${index}`}
      aria-labelledby={`fee-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ pt: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const FeeSchedule: React.FC = () => {
  const [currentTab, setCurrentTab] = useState(0);
  const [feeSchedules, setFeeSchedules] = useState<FeeScheduleType[]>([]);
  const [payerContracts, setPayerContracts] = useState<PayerContract[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedSchedule, setSelectedSchedule] = useState<string>('all');
  const [openScheduleDialog, setOpenScheduleDialog] = useState(false);
  const [selectedFeeSchedule, setSelectedFeeSchedule] = useState<FeeScheduleType | null>(null);
  const [openContractDialog, setOpenContractDialog] = useState(false);
  const [selectedContract, setSelectedContract] = useState<PayerContract | null>(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      // Mock data - in real app, these would be API calls
      const mockFeeSchedules: FeeScheduleType[] = [
        {
          id: 'fs-001',
          name: 'Medicare Fee Schedule 2024',
          description: 'Medicare Laboratory Fee Schedule for 2024',
          year: 2024,
          type: 'medicare',
          isActive: true,
          rates: [
            {
              cptCode: '85025',
              allowedAmount: 10.93,
              placeOfService: '11',
              effectiveDate: '2024-01-01T00:00:00Z'
            },
            {
              cptCode: '80053',
              allowedAmount: 13.89,
              placeOfService: '11',
              effectiveDate: '2024-01-01T00:00:00Z'
            }
          ],
          createdAt: '2024-01-01T00:00:00Z',
          updatedAt: '2024-01-01T00:00:00Z'
        },
        {
          id: 'fs-002',
          name: 'Commercial Rate Schedule',
          description: 'Standard commercial payer rates',
          year: 2024,
          type: 'commercial',
          isActive: true,
          rates: [
            {
              cptCode: '85025',
              allowedAmount: 45.00,
              placeOfService: '11',
              effectiveDate: '2024-01-01T00:00:00Z'
            },
            {
              cptCode: '80053',
              allowedAmount: 65.00,
              placeOfService: '11',
              effectiveDate: '2024-01-01T00:00:00Z'
            }
          ],
          createdAt: '2024-01-01T00:00:00Z',
          updatedAt: '2024-01-01T00:00:00Z'
        }
      ];

      const mockPayerContracts: PayerContract[] = [
        {
          id: 'pc-001',
          payerId: 'PAYER-001',
          payerName: 'Medicare',
          contractType: 'medicare',
          effectiveDate: '2024-01-01T00:00:00Z',
          feeScheduleId: 'fs-001',
          isActive: true,
          contractTerms: 'Standard Medicare terms and conditions',
          placeOfServiceRates: [
            {
              placeOfServiceCode: '11',
              description: 'Office',
              conversionFactor: 1.0,
              geographicAdjustmentFactor: 1.0
            }
          ],
          createdAt: '2024-01-01T00:00:00Z',
          updatedAt: '2024-01-01T00:00:00Z'
        },
        {
          id: 'pc-002',
          payerId: 'PAYER-002',
          payerName: 'Blue Cross Blue Shield',
          contractType: 'commercial',
          effectiveDate: '2024-01-01T00:00:00Z',
          expirationDate: '2026-12-31T23:59:59Z',
          feeScheduleId: 'fs-002',
          isActive: true,
          contractTerms: 'Commercial contract with annual rate increases',
          placeOfServiceRates: [
            {
              placeOfServiceCode: '11',
              description: 'Office',
              conversionFactor: 1.2,
              geographicAdjustmentFactor: 1.1
            }
          ],
          createdAt: '2024-01-01T00:00:00Z',
          updatedAt: '2024-01-01T00:00:00Z'
        }
      ];

      setFeeSchedules(mockFeeSchedules);
      setPayerContracts(mockPayerContracts);
    } catch (error) {
      console.error('Error loading fee schedule data:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  const getScheduleTypeColor = (type: string) => {
    switch (type) {
      case 'medicare':
        return 'primary';
      case 'medicaid':
        return 'secondary';
      case 'commercial':
        return 'success';
      case 'custom':
        return 'warning';
      default:
        return 'default';
    }
  };

  const getContractTypeColor = (type: string) => {
    switch (type) {
      case 'medicare':
        return 'primary';
      case 'medicaid':
        return 'secondary';
      case 'commercial':
        return 'success';
      case 'selfpay':
        return 'warning';
      default:
        return 'default';
    }
  };

  const handleEditSchedule = (schedule: FeeScheduleType) => {
    setSelectedFeeSchedule(schedule);
    setOpenScheduleDialog(true);
  };

  const handleAddSchedule = () => {
    setSelectedFeeSchedule(null);
    setOpenScheduleDialog(true);
  };

  const handleEditContract = (contract: PayerContract) => {
    setSelectedContract(contract);
    setOpenContractDialog(true);
  };

  const handleAddContract = () => {
    setSelectedContract(null);
    setOpenContractDialog(true);
  };

  // Filter rates based on search and selected schedule
  const getAllRates = () => {
    let allRates: (FeeScheduleRate & { scheduleName: string; scheduleType: string })[] = [];
    
    feeSchedules.forEach(schedule => {
      if (selectedSchedule === 'all' || selectedSchedule === schedule.id) {
        schedule.rates.forEach(rate => {
          allRates.push({
            ...rate,
            scheduleName: schedule.name,
            scheduleType: schedule.type
          });
        });
      }
    });

    if (searchTerm) {
      allRates = allRates.filter(rate => 
        rate.cptCode.includes(searchTerm) ||
        rate.scheduleName.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    return allRates;
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5">
          Fee Schedule & Pricing Engine
        </Typography>
        <Button
          variant="contained"
          startIcon={<RefreshIcon />}
          onClick={loadData}
          disabled={loading}
        >
          Refresh
        </Button>
      </Box>

      <Paper elevation={1} sx={{ mb: 3 }}>
        <Tabs
          value={currentTab}
          onChange={(e, newValue) => setCurrentTab(newValue)}
          variant="fullWidth"
        >
          <Tab 
            label="Fee Schedules" 
            icon={<ScheduleIcon />} 
            iconPosition="start"
          />
          <Tab 
            label="Rate Lookup" 
            icon={<TestIcon />} 
            iconPosition="start"
          />
          <Tab 
            label="Payer Contracts" 
            icon={<PayerIcon />} 
            iconPosition="start"
          />
        </Tabs>
      </Paper>

      <TabPanel value={currentTab} index={0}>
        {/* Fee Schedules Tab */}
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                  <Typography variant="h6">
                    Fee Schedule Management
                  </Typography>
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={handleAddSchedule}
                  >
                    Add Schedule
                  </Button>
                </Box>

                <Grid container spacing={2}>
                  {feeSchedules.map((schedule) => (
                    <Grid item xs={12} md={6} key={schedule.id}>
                      <Card variant="outlined">
                        <CardContent>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                            <Box>
                              <Typography variant="h6" gutterBottom>
                                {schedule.name}
                              </Typography>
                              <Typography variant="body2" color="text.secondary" gutterBottom>
                                {schedule.description}
                              </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', gap: 1 }}>
                              <Chip 
                                label={schedule.type.charAt(0).toUpperCase() + schedule.type.slice(1)}
                                color={getScheduleTypeColor(schedule.type) as any}
                                size="small"
                              />
                              <Chip 
                                label={schedule.isActive ? 'Active' : 'Inactive'}
                                color={schedule.isActive ? 'success' : 'default'}
                                size="small"
                              />
                            </Box>
                          </Box>

                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                            <Typography variant="body2" color="text.secondary">
                              Year: {schedule.year}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {schedule.rates.length} rates defined
                            </Typography>
                          </Box>

                          <Accordion>
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                              <Typography variant="body2">View Rates</Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                              <Table size="small">
                                <TableHead>
                                  <TableRow>
                                    <TableCell>CPT Code</TableCell>
                                    <TableCell>Place of Service</TableCell>
                                    <TableCell align="right">Allowed Amount</TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {schedule.rates.slice(0, 5).map((rate, index) => (
                                    <TableRow key={index}>
                                      <TableCell>{rate.cptCode}</TableCell>
                                      <TableCell>{rate.placeOfService}</TableCell>
                                      <TableCell align="right">
                                        {formatCurrency(rate.allowedAmount)}
                                      </TableCell>
                                    </TableRow>
                                  ))}
                                  {schedule.rates.length > 5 && (
                                    <TableRow>
                                      <TableCell colSpan={3} align="center" sx={{ fontStyle: 'italic', color: 'text.secondary' }}>
                                        ... and {schedule.rates.length - 5} more rates
                                      </TableCell>
                                    </TableRow>
                                  )}
                                </TableBody>
                              </Table>
                            </AccordionDetails>
                          </Accordion>

                          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                            <Tooltip title="Edit Schedule">
                              <IconButton 
                                size="small" 
                                onClick={() => handleEditSchedule(schedule)}
                              >
                                <EditIcon />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </TabPanel>

      <TabPanel value={currentTab} index={1}>
        {/* Rate Lookup Tab */}
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Rate Lookup & Comparison
                </Typography>

                <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
                  <TextField
                    label="Search CPT codes"
                    variant="outlined"
                    size="small"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon />
                        </InputAdornment>
                      )
                    }}
                    sx={{ minWidth: 250 }}
                  />
                  <FormControl size="small" sx={{ minWidth: 200 }}>
                    <InputLabel>Fee Schedule</InputLabel>
                    <Select
                      value={selectedSchedule}
                      label="Fee Schedule"
                      onChange={(e) => setSelectedSchedule(e.target.value)}
                    >
                      <MenuItem value="all">All Schedules</MenuItem>
                      {feeSchedules.map((schedule) => (
                        <MenuItem key={schedule.id} value={schedule.id}>
                          {schedule.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>

                <TableContainer component={Paper} variant="outlined">
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>CPT Code</TableCell>
                        <TableCell>Fee Schedule</TableCell>
                        <TableCell>Type</TableCell>
                        <TableCell>Place of Service</TableCell>
                        <TableCell align="right">Allowed Amount</TableCell>
                        <TableCell>Effective Date</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {getAllRates().map((rate, index) => (
                        <TableRow key={index} hover>
                          <TableCell>
                            <Typography variant="body2" fontWeight="medium">
                              {rate.cptCode}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {rate.scheduleName}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip 
                              label={rate.scheduleType.charAt(0).toUpperCase() + rate.scheduleType.slice(1)}
                              color={getScheduleTypeColor(rate.scheduleType) as any}
                              size="small"
                            />
                          </TableCell>
                          <TableCell>{rate.placeOfService}</TableCell>
                          <TableCell align="right">
                            <Typography variant="body2" fontWeight="medium">
                              {formatCurrency(rate.allowedAmount)}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            {formatDate(rate.effectiveDate)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </TabPanel>

      <TabPanel value={currentTab} index={2}>
        {/* Payer Contracts Tab */}
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                  <Typography variant="h6">
                    Payer Contract Management
                  </Typography>
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={handleAddContract}
                  >
                    Add Contract
                  </Button>
                </Box>

                <TableContainer component={Paper} variant="outlined">
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Payer Name</TableCell>
                        <TableCell>Contract Type</TableCell>
                        <TableCell>Fee Schedule</TableCell>
                        <TableCell>Effective Date</TableCell>
                        <TableCell>Expiration Date</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {payerContracts.map((contract) => (
                        <TableRow key={contract.id} hover>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <BusinessIcon sx={{ mr: 1, color: 'text.secondary' }} />
                              <Typography variant="body2" fontWeight="medium">
                                {contract.payerName}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Chip 
                              label={contract.contractType.charAt(0).toUpperCase() + contract.contractType.slice(1)}
                              color={getContractTypeColor(contract.contractType) as any}
                              size="small"
                            />
                          </TableCell>
                          <TableCell>
                            {feeSchedules.find(fs => fs.id === contract.feeScheduleId)?.name || 'Unknown'}
                          </TableCell>
                          <TableCell>
                            {formatDate(contract.effectiveDate)}
                          </TableCell>
                          <TableCell>
                            {contract.expirationDate ? formatDate(contract.expirationDate) : 'No expiration'}
                          </TableCell>
                          <TableCell>
                            <Chip 
                              label={contract.isActive ? 'Active' : 'Inactive'}
                              color={contract.isActive ? 'success' : 'default'}
                              size="small"
                            />
                          </TableCell>
                          <TableCell>
                            <Tooltip title="Edit Contract">
                              <IconButton 
                                size="small" 
                                onClick={() => handleEditContract(contract)}
                              >
                                <EditIcon />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </TabPanel>

      {/* Add/Edit Fee Schedule Dialog - Placeholder */}
      <Dialog 
        open={openScheduleDialog} 
        onClose={() => setOpenScheduleDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {selectedFeeSchedule ? 'Edit Fee Schedule' : 'Add New Fee Schedule'}
        </DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mt: 1 }}>
            Fee schedule creation/editing dialog would be implemented here with full form fields for schedule properties and rate management.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenScheduleDialog(false)}>Cancel</Button>
          <Button variant="contained">Save</Button>
        </DialogActions>
      </Dialog>

      {/* Add/Edit Payer Contract Dialog - Placeholder */}
      <Dialog 
        open={openContractDialog} 
        onClose={() => setOpenContractDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {selectedContract ? 'Edit Payer Contract' : 'Add New Payer Contract'}
        </DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mt: 1 }}>
            Payer contract creation/editing dialog would be implemented here with full form fields for contract properties and terms.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenContractDialog(false)}>Cancel</Button>
          <Button variant="contained">Save</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default FeeSchedule;